import { QueryClient } from "@tanstack/react-query";
import { addOrUpdateBooking } from "@/APIs/Booking/UpdateBooking";
import { ViewBooking, ViewBookingAssignedAreasInner } from "collins-js-sdk";
import { getBookingsListKey } from "@/queries/bookingsListCache";
import { AreaFromApi, UndoableAssignment } from "./SpaceScanner.types";
import type { BookingUpdateContext } from "@/utils/booking";

// The SDK types area id/name as object; undo sends full area objects, matching Stage 2 apply.
const toViewAssignedAreas = (
  areas: AreaFromApi[]
): ViewBookingAssignedAreasInner[] =>
  areas as unknown as ViewBookingAssignedAreasInner[];

export interface UndoOptimisationResult {
  revertedBookingIds: string[];
  failures: Array<{ assignment: UndoableAssignment; error: string }>;
}

export const undoOptimisation = async ({
  assignments,
  diaryContext,
  queryClient,
  onProgress
}: {
  assignments: UndoableAssignment[];
  diaryContext: Omit<BookingUpdateContext, "queryClient">;
  queryClient: QueryClient;
  onProgress?: (processed: number) => void;
}): Promise<UndoOptimisationResult> => {
  const revertedBookingIds: string[] = [];
  const failures: UndoOptimisationResult["failures"] = [];
  let processed = 0;
  const queryKey = getBookingsListKey(
    diaryContext.date,
    diaryContext.loadedVenueId ?? undefined
  );
  const diaryBookings = queryClient.getQueryData<ViewBooking[]>(queryKey);

  for (const assignment of assignments) {
    const baseBooking = diaryBookings?.find(
      (booking) => booking.id === assignment.bookingId
    );

    if (!baseBooking) {
      failures.push({
        assignment,
        error: "Booking is no longer in the diary"
      });
      processed++;
      onProgress?.(processed);
      continue;
    }

    try {
      const response = await addOrUpdateBooking({
        booking: {
          ...baseBooking,
          assignedAreas: toViewAssignedAreas(assignment.previousAreas)
        },
        diaryContext: { ...diaryContext, queryClient }
      });

      if (!response || response.unsaved) {
        failures.push({
          assignment,
          error: "Failed to revert booking"
        });
      } else {
        revertedBookingIds.push(assignment.bookingId);
      }
    } catch (error) {
      failures.push({
        assignment,
        error:
          error instanceof Error ? error.message : "Failed to revert booking"
      });
    }

    processed++;
    onProgress?.(processed);
  }

  return { revertedBookingIds, failures };
};
